/**
 * Application linéaire du plan — logique pure (Boîte à outils, prépa).
 *
 * Une matrice M = (a b ; c d) : ses COLONNES sont les images des vecteurs de
 * base, f(i) = (a ; c) et f(j) = (b ; d). Le carré unité devient le
 * parallélogramme construit sur f(i), f(j), d'aire |det M| = |ad − bc| ; un
 * déterminant négatif retourne l'orientation, nul écrase le plan sur une
 * droite (rang 1, noyau et image sont des droites) ou sur 0 (rang 0).
 * Les vecteurs propres restent sur leur droite : f(v) = λv, λ racine de
 * χ(λ) = λ² − (tr M)λ + det M.
 *
 * Les coefficients sont des multiples de 0,5 : traces, déterminants et
 * discriminants sont des décimaux exacts (calculés en quarts entiers).
 */

import { formatNumber } from '../../ui/format';
import type { Pt } from '../../ui/frame';

export interface Mat {
  a: number;
  b: number;
  c: number;
  d: number;
}

export const apply = (m: Mat, p: Pt): Pt => ({
  x: m.a * p.x + m.b * p.y,
  y: m.c * p.x + m.d * p.y,
});

/** valeurs exactes sur des quarts : on arrondit au 1/16 le résultat d'un produit de demis */
const q = (v: number) => Math.round(v * 16) / 16;

export const det = (m: Mat) => q(m.a * m.d - m.b * m.c);
export const trace = (m: Mat) => q(m.a + m.d);

/** « 0,5 », « −2 », « 1,25 » */
export function num(v: number, digits = 4): string {
  const r = Number(v.toFixed(10));
  return formatNumber(Object.is(r, -0) ? 0 : r, digits).replace('-', '−');
}

export interface Preset {
  id: string;
  label: string;
  m: Mat;
}

export const PRESETS: readonly Preset[] = [
  { id: 'id', label: 'Identité', m: { a: 1, b: 0, c: 0, d: 1 } },
  { id: 'rot', label: 'Rotation d’un quart de tour', m: { a: 0, b: -1, c: 1, d: 0 } },
  { id: 'sym', label: 'Symétrie (axe y = x)', m: { a: 0, b: 1, c: 1, d: 0 } },
  { id: 'proj', label: 'Projection sur (Ox) parallèlement à (Oy)', m: { a: 1, b: 0, c: 0, d: 0 } },
  { id: 'hom', label: 'Homothétie de rapport 2', m: { a: 2, b: 0, c: 0, d: 2 } },
  { id: 'shear', label: 'Transvection', m: { a: 1, b: 1, c: 0, d: 1 } },
  { id: 'diag', label: 'Deux droites propres', m: { a: 2, b: 1, c: 1, d: 2 } },
  { id: 'rank1', label: 'Rang 1', m: { a: 1, b: 2, c: 0.5, d: 1 } },
];

export type Orientation = 'direct' | 'indirect' | 'flat';

export const orientation = (m: Mat): Orientation =>
  det(m) > 0 ? 'direct' : det(m) < 0 ? 'indirect' : 'flat';

export const rank = (m: Mat) =>
  det(m) !== 0 ? 2 : m.a === 0 && m.b === 0 && m.c === 0 && m.d === 0 ? 0 : 1;

/** direction non nulle d'un vecteur, normalisée pour l'affichage (sinon null) */
const dir = (p: Pt): Pt | null => {
  const n = Math.hypot(p.x, p.y);
  return n < 1e-12 ? null : { x: p.x / n, y: p.y / n };
};

/** noyau et image d'une matrice de rang 1 (directions) */
export function kernelImage(m: Mat): { ker: Pt; im: Pt } | null {
  if (rank(m) !== 1) return null;
  // image : une colonne non nulle ; noyau : orthogonal à une ligne non nulle
  const im = dir({ x: m.a, y: m.c }) ?? dir({ x: m.b, y: m.d })!;
  const row = Math.hypot(m.a, m.b) > 0 ? { x: m.a, y: m.b } : { x: m.c, y: m.d };
  const ker = dir({ x: -row.y, y: row.x })!;
  return { ker, im };
}

export interface Eigen {
  /** valeur propre (approchée si irrationnelle) */
  value: number;
  /** écriture : « 3 », « (3 + √5)/2 » */
  text: string;
  /** direction propre (vecteur unitaire), ou null si tout le plan est propre */
  dir: Pt | null;
}

const gcd = (x: number, y: number): number => {
  x = Math.abs(x);
  y = Math.abs(y);
  while (y) [x, y] = [y, x % y];
  return x;
};

/**
 * écriture exacte de λ = (t ± √D)/2 : décimale si D est un carré, sinon
 * « (1 + √5)/2 », « (1 − √17)/4 », « −√2 » — jamais de décimale dans la racine.
 * t est un multiple de 1/2 et D de 1/16 : avec T = 2t et k = 16D entiers,
 * λ = (2T ± √k)/8 = (2T ± p√r)/8, simplifié par pgcd(2T, p, 8).
 */
function rootText(t: number, D: number, sign: 1 | -1): { value: number; text: string } {
  const value = (t + sign * Math.sqrt(D)) / 2;
  const k = Math.round(D * 16);
  const s = Math.round(Math.sqrt(k));
  if (s * s === k) return { value, text: num(value) };
  let p = 1;
  let r = k;
  for (let f = 2; f * f <= r; f++)
    while (r % (f * f) === 0) {
      r /= f * f;
      p *= f;
    }
  const T2 = Math.round(4 * t);
  const g = gcd(gcd(T2, p), 8) || 1;
  const a = T2 / g;
  const b = p / g;
  const den = 8 / g;
  const root = `${b === 1 ? '' : num(b)}√${r}`;
  if (a === 0) return { value, text: `${sign < 0 ? '−' : ''}${root}${den === 1 ? '' : `/${den}`}` };
  const body = `${num(a)} ${sign > 0 ? '+' : '−'} ${root}`;
  return { value, text: den === 1 ? body : `(${body})/${den}` };
}

export interface Spectrum {
  tr: number;
  det: number;
  /** discriminant de χ */
  disc: number;
  eigen: Eigen[];
  /** tout le plan est propre (M = λI) */
  scalar: boolean;
  /** diagonalisable sur ℝ */
  diagonalisable: boolean;
}

/** direction propre pour λ : un vecteur non nul de Ker(M − λI) */
function eigenDir(m: Mat, l: number): Pt | null {
  const a = m.a - l;
  const d = m.d - l;
  // (a b ; c d)·v = 0 : v ⟂ ligne non nulle
  if (Math.hypot(a, m.b) > 1e-9) return dir({ x: -m.b, y: a });
  if (Math.hypot(m.c, d) > 1e-9) return dir({ x: -d, y: m.c });
  return null;
}

export function spectrum(m: Mat): Spectrum {
  const tr = trace(m);
  const dt = det(m);
  const disc = q(tr * tr - 4 * dt);
  const scalar = m.b === 0 && m.c === 0 && m.a === m.d;
  if (disc < 0) return { tr, det: dt, disc, eigen: [], scalar: false, diagonalisable: false };
  if (disc === 0) {
    const l = tr / 2;
    return {
      tr,
      det: dt,
      disc,
      eigen: [{ value: l, text: num(l), dir: scalar ? null : eigenDir(m, l) }],
      scalar,
      diagonalisable: scalar,
    };
  }
  const roots = [rootText(tr, disc, -1), rootText(tr, disc, 1)];
  return {
    tr,
    det: dt,
    disc,
    eigen: roots.map((r) => ({ ...r, dir: eigenDir(m, r.value) })),
    scalar: false,
    diagonalisable: true,
  };
}

/** χ(λ) = λ² − (tr)λ + det, écrit */
export function charPolyText(m: Mat): string {
  const tr = trace(m);
  const dt = det(m);
  const mid =
    tr === 0 ? '' : tr > 0 ? ` − ${tr === 1 ? '' : num(tr)}λ` : ` + ${tr === -1 ? '' : num(-tr)}λ`;
  const end = dt === 0 ? '' : dt > 0 ? ` + ${num(dt)}` : ` − ${num(-dt)}`;
  return `χ(λ) = λ²${mid}${end}`;
}

/** v et f(v) colinéaires ? (déterminant nul) */
export const collinear = (v: Pt, w: Pt) => Math.abs(v.x * w.y - v.y * w.x) < 1e-9;

/* ---------- défi ---------- */

export type QuizKind = 'matrix' | 'det';

export interface QuizOption {
  text: string;
  m?: Mat;
  correct: boolean;
  hint?: string;
}

export interface Quiz {
  kind: QuizKind;
  m: Mat;
  options: QuizOption[];
}

const pick = <T>(xs: readonly T[], rnd: () => number): T => xs[Math.floor(rnd() * xs.length)]!;

function shuffle<T>(xs: T[], rnd: () => number): T[] {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export const matText = (m: Mat) => `(${num(m.a)} ${num(m.b)} ; ${num(m.c)} ${num(m.d)})`;

const same = (x: Mat, y: Mat) => x.a === y.a && x.b === y.b && x.c === y.c && x.d === y.d;

/** une matrice entière lisible, |coef| ≤ 2, ni symétrique ni diagonale (sinon les pièges coïncident) */
function randomMat(rnd: () => number): Mat {
  for (let k = 0; k < 200; k++) {
    const v = () => pick([-2, -1, 0, 1, 2] as const, rnd);
    const m = { a: v(), b: v(), c: v(), d: v() };
    if (m.b === m.c || m.a === m.d) continue;
    if (det(m) === 0) continue;
    if (Math.abs(m.a * m.d) === Math.abs(m.b * m.c)) continue;
    return m;
  }
  return { a: 1, b: 2, c: -1, d: 0 };
}

export function makeQuiz(kind: QuizKind, rnd: () => number): Quiz {
  const m = randomMat(rnd);
  if (kind === 'matrix') {
    const t = { a: m.a, b: m.c, c: m.b, d: m.d };
    const swap = { a: m.b, b: m.a, c: m.d, d: m.c };
    const neg = { a: -m.a, b: m.b, c: -m.c, d: m.d };
    const cands: QuizOption[] = [
      { text: matText(m), m, correct: true },
      {
        text: matText(t),
        m: t,
        correct: false,
        hint: 'f(i) et f(j) sont écrits en lignes : dans la matrice, l’image de i s’écrit en première colonne, celle de j en seconde colonne.',
      },
      {
        text: matText(swap),
        m: swap,
        correct: false,
        hint: 'Colonnes échangées : la première colonne est f(i), la seconde f(j).',
      },
      {
        text: matText(neg),
        m: neg,
        correct: false,
        hint: 'Regarde le sens de f(i) : ses coordonnées se lisent sur la figure, signes compris.',
      },
    ];
    const uniq = cands.filter((o, i) => cands.findIndex((x) => same(x.m!, o.m!)) === i);
    return { kind, m, options: shuffle(uniq, rnd) };
  }
  const dt = det(m);
  const vals: Array<[number, string | undefined]> = [
    [dt, undefined],
    [
      -dt,
      'Le signe dit l’orientation : regarde si f(i), f(j) tournent dans le même sens que i, j.',
    ],
    [q(m.a * m.d + m.b * m.c), 'det M = ad − bc : un signe moins entre les deux produits.'],
    [q(m.a * m.b - m.c * m.d), 'On multiplie en croix les coefficients de la matrice : ad − bc.'],
    [q(m.a * m.d), 'Il manque le produit bc : det M = ad − bc.'],
    [q(m.a * m.c - m.b * m.d), 'On multiplie en croix : a par d, b par c.'],
    [q(dt + 1), 'Recompte : det M = ad − bc.'],
    [q(dt - 1), 'Recompte : det M = ad − bc.'],
    [q(dt + 2), 'Recompte : det M = ad − bc.'],
  ];
  const seen = new Set<number>();
  const options: QuizOption[] = [];
  for (const [v, hint] of vals) {
    if (seen.has(v) || options.length >= 4) continue;
    seen.add(v);
    options.push({ text: num(v), correct: !hint, ...(hint ? { hint } : {}) });
  }
  return { kind, m, options: shuffle(options, rnd) };
}

export const QUIZ_KINDS: readonly QuizKind[] = ['matrix', 'det', 'matrix', 'det', 'matrix'];
